/**
 * Permissions surface — re-exports of the canonical types and lookup tables
 * derived from `shared/permission-records.ts` (Tech Debt I, Task #1726).
 *
 * The single source of truth for permission keys, default role assignments,
 * non-delegatable flags, and feature gating is `PERMISSION_RECORDS`. Do NOT
 * hand-maintain a duplicate `Permission` union or `ROLE_PERMISSIONS` literal
 * here — both are derived programmatically below.
 */
export { NON_DELEGATABLE_PERMISSIONS } from "./permission-catalog";
export { BuiltInRoles, Scopes, type ScopeValue } from "./constants/roles";
import { BuiltInRoles, ROLE_ALIASES as CENTRALIZED_ROLE_ALIASES, isSuperAdminRole, resolveRoleAlias as centralResolveRoleAlias } from "./constants/roles";
import { PERMISSION_RECORDS } from "./permission-records";

export type UserRole = string;

/**
 * Canonical permission union — derived from `PERMISSION_RECORDS[*].key`.
 * Adding a permission is therefore a single-file edit in
 * `shared/permission-records.ts`; the union (and the matching
 * `ROLE_PERMISSIONS` derivation below) update automatically.
 */
export type Permission = typeof PERMISSION_RECORDS[number]["key"];

export const BUILT_IN_ROLES = [
  BuiltInRoles.SUPER_ADMIN,
  BuiltInRoles.TENANT_ADMIN,
  BuiltInRoles.HR_MANAGER,
  BuiltInRoles.SITE_MANAGER,
  BuiltInRoles.HSE_OFFICER,
  BuiltInRoles.SUPERVISOR,
  BuiltInRoles.OPS_LEAD,
  BuiltInRoles.VIEWER,
  BuiltInRoles.ISSUER,
  BuiltInRoles.WORKER,
] as const;
export type BuiltInRole = typeof BUILT_IN_ROLES[number];

/**
 * `ROLE_PERMISSIONS` is derived from `PERMISSION_RECORDS[*].defaultRoles`.
 * For each built-in role we collect every permission record whose
 * `defaultRoles` array includes that role, in catalog (declaration) order.
 *
 * Invariants enforced by `shared/__tests__/role-policy-registry.test.ts`:
 *   - Every built-in role appears in this map.
 *   - Every permission key referenced by a role exists in PERMISSION_CATALOG.
 *   - Every PERMISSION_CATALOG entry is granted to at least one built-in role.
 *   - Per-role lists contain no duplicates.
 *   - Super Admin holds every catalog permission.
 */
function buildRolePermissions(): Record<BuiltInRole, Permission[]> {
  const out = {} as Record<BuiltInRole, Permission[]>;
  for (const role of BUILT_IN_ROLES) {
    out[role] = PERMISSION_RECORDS
      .filter((p) => p.defaultRoles.includes(role))
      .map((p) => p.key as Permission);
  }
  return out;
}

export const ROLE_PERMISSIONS: Record<BuiltInRole, Permission[]> = buildRolePermissions();

export const ALL_ROLES: BuiltInRole[] = [...BUILT_IN_ROLES];

export function isSuperAdmin(role: string | null | undefined): boolean {
  return isSuperAdminRole(role);
}

const ROLE_ALIASES: Record<string, BuiltInRole> = CENTRALIZED_ROLE_ALIASES as Record<string, BuiltInRole>;

export function resolveRoleAlias(role: string | null | undefined): string {
  return centralResolveRoleAlias(role);
}

export function getPermissionsForRole(role: string | null): Permission[] {
  if (!role) return [];
  const resolvedRole = ROLE_ALIASES[role] ?? role;
  return ROLE_PERMISSIONS[resolvedRole as BuiltInRole] || [];
}

export function roleHasPermission(role: string | null, permission: Permission): boolean {
  return getPermissionsForRole(role).includes(permission);
}

export function getRoleDashboard(role: string | undefined | null): string {
  switch (role) {
    case BuiltInRoles.SUPER_ADMIN:
      return "/super-admin/dashboard";
    case BuiltInRoles.HSE_OFFICER:
      return "/safety/dashboard";
    case BuiltInRoles.SUPERVISOR:
      return "/supervisor/workforce-readiness";
    case BuiltInRoles.OPS_LEAD:
      return "/operations/dashboard";
    case "Partner":
      return "/partner/home";
    default:
      return "/tenant/dashboard";
  }
}
